import type { DecoratedCardVariant } from '../DecoratedCard/DecoratedCard';
import type { ReactNode } from 'react';
import { DecoratedCard } from '../DecoratedCard/DecoratedCard';
import { Typography } from '../Typography/Typography';
import { ParticipantDecoration, type ParticipantDecorationVariant } from './ParticipantDecoration';
import './PersonCard.css';

export interface PersonCardProps {
  avatar?: ReactNode;
  name?: ReactNode;
  title?: ReactNode;
  status?: ReactNode;
  actions?: ReactNode;
  children?: ReactNode;
  editAction?: ReactNode;
  layout?: 'exchange' | 'detail';
  compact?: boolean;
  decoration?: ParticipantDecorationVariant;
  edges?: false | DecoratedCardVariant;
  onClick?: () => void;
  className?: string;
  testId?: string;
}
/** One surface and identity layout for people, with optional content and actions. */
export function PersonCard({
  avatar,
  name,
  title,
  status,
  actions,
  children,
  editAction,
  layout = 'detail',
  compact = false,
  decoration = 'forest',
  edges = 1,
  onClick,
  className = '',
  testId,
}: PersonCardProps) {
  return (
    <DecoratedCard
      edges={edges !== false}
      variant={edges || 1}
      motif={false}
      className={`person-card person-card--${layout}${compact ? ' person-card--compact' : ''} ${className}`}
    >
      <ParticipantDecoration variant={decoration} />
      <article
        className="person-card__body"
        data-testid={testId}
        role={onClick ? 'link' : undefined}
        tabIndex={onClick ? 0 : undefined}
        onClick={onClick}
        onKeyDown={
          onClick
            ? (event) => {
                if (
                  event.target === event.currentTarget &&
                  (event.key === 'Enter' || event.key === ' ')
                ) {
                  event.preventDefault();
                  onClick();
                }
              }
            : undefined
        }
      >
        {editAction}
        {(avatar || name) && (
          <header className="person-card__identity">
            {avatar && <span className="person-card__avatar">{avatar}</span>}
            <div className="person-card__copy">
              {title && <span className="person-card__title">{title}</span>}
              <Typography as="h2" role="title/card" color="inherit">
                {name}
              </Typography>
              {status}
              {layout === 'exchange' && actions && (
                <div className="person-card__actions">{actions}</div>
              )}
            </div>
          </header>
        )}
        {children && <div className="person-card__content">{children}</div>}
        {layout !== 'exchange' && actions && <div className="person-card__actions">{actions}</div>}
      </article>
    </DecoratedCard>
  );
}
